import React from "react";
import { useEffect } from "react";
import { toast } from "react-toastify";
import parse from "html-react-parser";
import { getFailedTenants } from "../api";
import { useApplicationContext } from "../context/ApplicationContext";
import { createLogger } from '../utils/logging';

const logger = createLogger('useFailedTenants');

/**
 * useFailedTenants - Fetches and displays failed/disabled tenants
 * 
 * Fetches list of failed tenants from the API and displays a toast notification
 * Stores the list in ApplicationContext for reference
 */
export const useFailedTenants = () => {
  const { dispatch, abilitiesLoaded, currentTenant } = useApplicationContext();

  useEffect(() => {
    // Wait until auth initialization is complete (abilities loaded) before
    // calling the failed-tenants endpoint — it routes through the secure auth
    // proxy and will 401 if called before the session is fully established.
    if (!abilitiesLoaded || !currentTenant) return;

    const fetchFailedTenants = async () => {
      try {
        const data = await getFailedTenants(currentTenant);
        const failedTenantsList = data?.failedTenants?.map(t => t.tenantId) || [];
        
        // Store in context
        dispatch({ type: "SET_FAILED_TENANTS", failedTenants: failedTenantsList });

        // Display toast notification if there are failed tenants
        if (failedTenantsList && failedTenantsList.length > 0) {
          const toastFailedList = failedTenantsList.join("<br>");
          const toastContent = `<br><div>${toastFailedList}</div>`;

          toast.info(
            <div>Disabled Tenants: {parse(toastContent)}</div>,
            {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            }
          );
        }
      } catch (error) {
        logger.warn('Fetch Failed', 'Unable to retrieve failed tenants', { error });
        // Silently fail - this is not critical to app functionality
      }
    };

    fetchFailedTenants();
  }, [dispatch, abilitiesLoaded, currentTenant]);
};
